import type { ProgressionTier } from '@worksim/engine';
import { href } from '../router';

export function StatusBadge({ status }: { status: 'available' | 'planned' }) {
  return <span className={`badge ${status}`}>{status === 'available' ? 'Simulation available' : 'Exploration only (simulation planned)'}</span>;
}

export function TierBadge({ tier }: { tier: ProgressionTier | string }) {
  return <span className="badge tier">{tier}</span>;
}

export function Meter({ value, label }: { value: number; label: string }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div>
      <div className="small muted">{label}: {pct}%</div>
      <div className={`meter ${value >= 0.7 ? 'ok' : value < 0.4 ? 'bad' : ''}`} role="img" aria-label={`${label} ${pct} percent`}>
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Announced loading state. */
export function Loading({ what = 'Loading' }: { what?: string }) {
  return (
    <p className="muted" role="status" aria-live="polite">
      {what}…
    </p>
  );
}

/** An error the person can act on. Never receives a raw error object: pass the message. */
export function ErrorNotice({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  if (!message) return null;
  return (
    <div className="notice bad" role="alert">
      {message}{' '}
      {onRetry && (
        <button type="button" className="quiet" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * The sign-in gate. Inline on a page that already has its own heading it is an `h2`; as the whole
 * page (the app-level guard for a signed-in route) it is the page's `h1`.
 */
export function SignInPrompt({ what = 'this page', page = false }: { what?: string; page?: boolean }) {
  const Heading = page ? 'h1' : 'h2';
  return (
    <section className="card" aria-labelledby="signin-title">
      <Heading id="signin-title" style={{ marginTop: 0 }}>Sign in to use {what}</Heading>
      <p>Your progress, sessions and workplace live in your WorkSim account. You can still explore and compare careers without signing in.</p>
      <div className="actions">
        <a className="button" href={href({ page: 'login' })}>Log in</a>
        <a className="button secondary" href={href({ page: 'register' })}>Create an account</a>
      </div>
    </section>
  );
}

export function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function formatDay(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

export const pct = (n: number | undefined) => (n === undefined ? '—' : `${Math.round(n * 100)}%`);

export const humanize = (s: string) => s.replace(/[-_.]/g, ' ');
